import React, { FunctionComponent } from 'react';

import { Slide } from '@domains/main-page';

import { mergeResponsiveImageSets } from '@utils/picture';

import { useEmbla } from '@hooks/use-embla';

import { Container } from '@components/common/Container/Container';
import { ImagePreload } from '@components/common/ImagePreload/ImagePreload';
import { Link } from '@components/common/Link/Link';
import { Picture } from '@components/common/Picture/Picture';
import { SliderNavigation } from '@components/common/SliderNavigation/SliderNavigation';
import { SliderPagination } from '@components/common/SliderPagination/SliderPagination';

import styles from './HeroSlider.module.scss';

type HeroSliderProps = {
    slides: Slide[];
};
export const HeroSlider: FunctionComponent<HeroSliderProps> = (props) => {
    const { slides } = props;

    const hasSlides = slides.length > 0;
    const hasMoreThanOneSlide = slides.length > 1;

    const { scrollTo, scrollSnaps, scrollPrev, scrollNext, viewportRef, selectedIndex } = useEmbla({
        loop: true,
        startIndex: 0,
        slidesToScroll: 1,
        speed: 5,
        containScroll: 'keepSnaps',
    });

    return hasSlides ? (
        <div className={styles.root}>
            <Container classes={{ root: styles.container }}>
                <div className={styles.slider}>
                    <div ref={viewportRef} className={styles.viewport}>
                        <div className={styles.sliderContainer}>
                            {slides.map(({ url, image, image_mobile }, index) => {
                                const sources = mergeResponsiveImageSets(image, image_mobile);

                                return (
                                    <React.Fragment key={index}>
                                        {index === 0 && <ImagePreload sources={sources} />}
                                        <Link
                                            className={styles.slide}
                                            route={url ? url : ''}
                                            target="_blank"
                                        >
                                            <Picture
                                                priority={index === 0}
                                                sources={sources}
                                                className={styles.image}
                                            />
                                        </Link>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    {hasMoreThanOneSlide && (
                        <SliderNavigation
                            onPrev={scrollPrev}
                            onNext={scrollNext}
                            variation="full"
                            classes={{ button: styles.navigationButton }}
                        />
                    )}
                </div>

                {hasMoreThanOneSlide && (
                    <SliderPagination
                        scrollSnaps={scrollSnaps}
                        scrollTo={scrollTo}
                        selectedIndex={selectedIndex}
                    />
                )}
            </Container>
        </div>
    ) : null;
};
